import React, { useState } from 'react';
import {
  Table,
  Button,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Modal,
  CustomInput,
} from 'reactstrap';
import { TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import * as ROLES from '../../../constants/roles';
import ModalConsumer from '../../utility/Modal/ModalConsumer';
import { updateDoc, deleteDoc } from 'firebase/firestore';
import { safeMigration } from '../../../config/featureFlags';
import { useToast } from '../../../contexts/ToastContext';

const useStyles = makeStyles((theme) => ({
  userSearchInput: {
    '& input': {
      color: 'white',
      backgroundColor: 'transparent',
      minHeight: '32px',
      fontFamily: 'Poppins',
      padding: '4px 8px',
      fontSize: 'var(--menu-font-size)'
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'transparent',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255, 255, 255, 0.1) !important',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255, 255, 255, 0.2) !important',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255, 255, 255, 0.2) !important',
      },
    },
    '& input::placeholder': {
      color: 'rgba(255,255,255,0.5)',
      opacity: 1,
      fontFamily: 'Poppins',
    }
  }
}));

export const ModalExample = (props) => {
  const [modal, setModal] = useState(props.isOpen);
  const toggle = () => {
    setModal(!modal);
  };
  const remove = () => {
    props.onRequestClose();
  };
  const callback = () => {
    props.callback();
    toggle();
  };
  return (
    <div>
      <Modal isOpen={modal} toggle={toggle} onClosed={remove} contentClassName="delete-confirm-modal">
        <ModalHeader toggle={toggle}>{props.title}</ModalHeader>
        <ModalBody>{props.body}</ModalBody>
        <ModalFooter>
          <Button 
            className="ml-auto btn-warning-custom" 
            onClick={callback}
            style={{ position: 'relative', zIndex: 2 }}
          >
            <span style={{ position: 'relative', zIndex: 3 }}>Continue</span>
          </Button>
          <Button 
            className="ml-2 btn-primary-custom" 
            onClick={toggle}
            style={{ position: 'relative', zIndex: 2 }}
          >
            <span style={{ position: 'relative', zIndex: 3 }}>Cancel</span>
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export const SuccessModal = (props) => {
  const [modal, setModal] = useState(props.isOpen);
  const toggle = () => {
    setModal(!modal);
  };
  const remove = () => {
    props.onRequestClose();
  };
  const callback = () => {
    if (props.callback) {
      props.callback();
    }
    toggle();
  };
  return (
    <div>
      <Modal isOpen={modal} toggle={toggle} onClosed={remove} contentClassName="success-modal">
        <ModalHeader toggle={toggle}>{props.title}</ModalHeader>
        <ModalBody>{props.body}</ModalBody>
        <ModalFooter>
          <Button
            className="ml-auto btn-primary-custom"
            onClick={callback}
            style={{ position: 'relative', zIndex: 2 }}
          >
            <span style={{ position: 'relative', zIndex: 3 }}>OK</span>
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export const WarningModal = (props) => {
  const [modal, setModal] = useState(props.isOpen);
  const toggle = () => {
    setModal(!modal);
  };
  const remove = () => {
    props.onRequestClose();
  };
  const callback = () => {
    if (props.callback) {
      props.callback();
    }
    toggle();
  };
  return (
    <div>
      <Modal isOpen={modal} toggle={toggle} onClosed={remove} contentClassName="warning-modal">
        <ModalHeader toggle={toggle}>{props.title}</ModalHeader>
        <ModalBody>{props.body}</ModalBody>
        <ModalFooter>
          <Button
            className="ml-auto btn-warning-custom"
            onClick={callback}
            style={{ position: 'relative', zIndex: 2 }}
          >
            <span style={{ position: 'relative', zIndex: 3 }}>OK</span>
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

const TableHeader = ({ label, sortKey, orderBy, onSort }) => {
  const isActive = orderBy.key === sortKey;
  const sortIcon = isActive ? (orderBy.direction === 'asc' ? '↓' : '↑') : '↕';
  
  return (
    <th>
      <h4 onClick={() => onSort(sortKey)} className="mb-0">
        {label} {sortIcon}
      </h4>
    </th>
  );
};

const UserList = ({ users, firebase }) => {
  const classes = useStyles();
  const [orderBy, setOrderBy] = useState({ key: 'email', direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');
  const { showSuccess, showError } = useToast();

  const handleSort = (key) => {
    setOrderBy(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value.toLowerCase());
  };

  const filteredUsers = [...users].filter(user => {
    if (!searchTerm) return true;
    return (
      user.email?.toLowerCase().includes(searchTerm) ||
      user.username?.toLowerCase().includes(searchTerm) ||
      (user.roles?.[ROLES.ADMIN] || 'USER')?.toLowerCase().includes(searchTerm)
    );
  });

  const sortedUsers = filteredUsers.sort((a, b) => {
    const aValue = a[orderBy.key] || '';
    const bValue = b[orderBy.key] || '';
    
    if (orderBy.key === 'roles') {
      const aRole = a.roles?.[ROLES.ADMIN] || 'USER';
      const bRole = b.roles?.[ROLES.ADMIN] || 'USER';
      return orderBy.direction === 'asc' 
        ? aRole.localeCompare(bRole)
        : bRole.localeCompare(aRole);
    }
    
    return orderBy.direction === 'asc'
      ? aValue.toString().localeCompare(bValue.toString())
      : bValue.toString().localeCompare(aValue.toString());
  });

  const deleteUser = async (userId, username) => {
    try {
      await safeMigration(
        'useUserManagement',
        // New hook method - call cloud function to delete both auth and firestore
        async () => {
          // Call the Firebase deleteUser method which uses the cloud function
          await firebase.deleteUser(userId);
        },
        // Original method (fallback)
        async () => {
          // Call the Firebase deleteUser method which uses the cloud function
          await firebase.deleteUser(userId);
        },
        'Users-deleteUser'
      );

      showSuccess(`User ${username} has been deleted`);
      // Small delay before reload to allow toast to show
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      console.error('Error deleting user:', err);

      // More detailed error handling
      let errorMessage = 'Unknown error';
      if (err.message) {
        errorMessage = err.message;
      }

      if (err.code) {
        errorMessage += ` (Code: ${err.code})`;
      }

      showError(`Error deleting user: ${errorMessage}`);
    }
  };

  const deleteOrphanedUser = async (userId, username) => {
    try {
      await safeMigration(
        'useUserManagement',
        // New hook method - only delete Firestore document for orphaned users
        async () => {
          const userDocRef = firebase.user(userId);
          await deleteDoc(userDocRef);
        },
        // Original method (fallback)
        async () => {
          const userDocRef = firebase.user(userId);
          await deleteDoc(userDocRef);
        },
        'Users-deleteOrphanedUser'
      );

      showSuccess(`User ${username} has been removed from the database`);
      // Small delay before reload to allow toast to show
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      console.error('Error deleting orphaned user:', err);
      showError(`Error deleting orphaned user: ${err.message}`);
    }
  };

  const updateUserRole = async (userId, newRole, username) => {
    try {
      await safeMigration(
        'useUserManagement',
        // New hook method - for now, use fallback as hooks need to be called at component level
        async () => {
          const userRef = firebase.user(userId);
          await updateDoc(userRef, {
            roles: {
              [ROLES.ADMIN]: newRole,
            },
          });
        },
        // Original method (fallback)
        async () => {
          const userRef = firebase.user(userId);
          await updateDoc(userRef, {
            roles: {
              [ROLES.ADMIN]: newRole,
            },
          });
        },
        'Users-updateUserRole'
      );
      showSuccess(`${username}'s role updated to ${newRole}`);
    } catch (err) {
      console.error('Error updating user role:', err);
      showError(`Error updating role: ${err.message}`);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: '1rem' }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search users by email, username, or role..."
          value={searchTerm}
          onChange={handleSearch}
          className={classes.userSearchInput}
        />
      </div>
      <div className="table-scroll-container" style={{ maxHeight: '600px', overflowY: 'auto', overflowX: 'auto' }}>
        <Table className="table-tracks" responsive>
        <thead>
          <tr>
            <TableHeader 
              label="Email" 
              sortKey="email" 
              orderBy={orderBy} 
              onSort={handleSort} 
            />
            <TableHeader 
              label="User Name" 
              sortKey="username" 
              orderBy={orderBy} 
              onSort={handleSort} 
            />
            <TableHeader
              label="Role"
              sortKey="roles"
              orderBy={orderBy}
              onSort={handleSort}
            />
            <th><h4 className="mb-0">Organizations</h4></th>
            <th><h4 className="mb-0">Super Admin</h4></th>
            <th>&nbsp;</th>
          </tr>
        </thead>
        <tbody>
          {sortedUsers.map((user, idx) => (
            <tr key={user.uid} className={idx % 2 === 0 ? 'table-alt-row' : ''}>
              <td>{user.email}</td>
              <td>{user.username}</td>
              <td>
                <CustomInput
                  type="select"
                  id={`role-select-${user.uid}`}
                  value={user.roles?.[ROLES.ADMIN] || "USER"}
                  onChange={(e) => updateUserRole(user.uid, e.target.value, user.username || user.email)}
                  style={{
                    backgroundColor: 'transparent',
                    color: '#ffffff',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: 'var(--border-radius-sm)',
                    padding: '4px 24px 4px 8px',
                    cursor: 'pointer',
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='4' height='5' viewBox='0 0 4 5'%3e%3cpath fill='%23ffffff' d='M2 0l2 2H0zm0 5L0 3h4z'/%3e%3c/svg%3e")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 0.5rem center',
                    backgroundSize: '12px 10px',
                    appearance: 'none',
                    fontSize: '14px',
                    fontFamily: 'var(--font-family-primary)',
                    transition: 'all var(--transition-fast)',
                    outline: 'none'
                  }}
                  className="role-select-custom"
                >
                  <option value="ADMIN">Admin</option>
                  <option value="OPERATOR">Operator</option>
                  <option value="USER">User</option>
                </CustomInput>
              </td>
              <td>
                {user.organizations?.length > 0
                  ? user.organizations.map(org => org.orgName).join(', ')
                  : '-'}
              </td>
              <td style={{ textAlign: 'center' }}>
                {user.isSuperAdmin ? (
                  <span style={{ color: '#ff6b6b', fontWeight: 'bold' }}>Yes</span>
                ) : (
                  <span style={{ color: 'rgba(255,255,255,0.5)' }}>No</span>
                )}
              </td>
              <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                <ModalConsumer>
                  {({ showModal }) => (
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                      <Button
                        className="btn-primary-custom btn-round"
                        size="sm"
                        style={{"display": "inline-block"}}
                        onClick={() => {
                          showModal(ModalExample, {
                            isOpen: true,
                            title: 'Delete User',
                            body: `Are you sure you want to delete ${user.username}? This will remove the user from both Authentication and Database.`,
                            callback: () => {
                              deleteUser(user.uid, user.username || user.email);
                            },
                          });
                        }}
                      >
                        <span className="d-flex flex-row align-items-center my-auto">
                          <i className="tim-icons icon-trash-simple "></i>{' '}
                          Delete
                        </span>
                      </Button>

                      <Button
                        className="btn-warning-custom btn-round"
                        size="sm"
                        style={{"display": "inline-block"}}
                        onClick={() => {
                          showModal(ModalExample, {
                            isOpen: true,
                            title: 'Delete Database Record',
                            body: `Are you sure you want to delete ${user.username} from the database only? Use this for orphaned users that don't exist in Authentication.`,
                            callback: () => {
                              deleteOrphanedUser(user.uid, user.username || user.email);
                            },
                          });
                        }}
                      >
                        <span className="d-flex flex-row align-items-center my-auto">
                          <i className="tim-icons icon-simple-remove "></i>{' '}
                          DB Only
                        </span>
                      </Button>
                    </div>
                  )}
                </ModalConsumer>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      </div>
    </div>
  );
};

export default UserList;
